import { useCallback, useMemo, useState } from 'react';
import {
  PublicationFeedback,
  PublicationFeedbackState,
  UserRoles,
} from '@amzn/awscat-pattern-library-service-model';
import { DateTime } from 'luxon';

import { FeedbackUpdate, PublishedVersion } from './UpdateRequestsTabViewModel';
import { isFeedbackClosed } from '../utils/DisplayNames';
import { useCoAuthors } from '../v2-hooks/useCoAuthors';
import { useContentEditorService } from '../../contexts/ContentEditorServiceContext';
import { useCurrentUser } from '../../contexts/UserContext';
import { useMplServiceClient } from '../../contexts/MplServiceClientContext';
import { log } from '../../loggers/Logger';
import { useLoadedDocument } from '../v2-hooks/useLoadedDocument';

export const COMMENT_MIN_LENGTH = 5;
export const COMMENT_MAX_LENGTH = 500;

/** Status options offered to authors (excludes the current state). */
const STATUS_OPTIONS: readonly {
  readonly value: PublicationFeedbackState;
  readonly label: string;
}[] = [
  { value: PublicationFeedbackState.CREATED, label: 'Assigned' },
  { value: PublicationFeedbackState.PENDING, label: 'In progress' },
  { value: PublicationFeedbackState.RESOLVED, label: 'Resolved' },
  { value: PublicationFeedbackState.NO_CHANGE_NEEDED, label: 'No change needed' },
];

/** Presentation state and commands for editing a single update-request item. */
export interface UpdateRequestItemViewModel {
  /** Current (optimistic) feedback state. */
  readonly state: PublicationFeedbackState;
  /** True when the item is in a terminal/closed state. */
  readonly isClosed: boolean;
  /** Whether the current user may change status (author/co-author/admin) on open feedback. */
  readonly canChangeStatus: boolean;
  /** Whether the current user may reopen closed feedback (author/co-author/admin). */
  readonly canReopen: boolean;
  /** Name of who opened the request, e.g. "Content freshness scan". */
  readonly submitterName: string;
  /** When the request was opened, e.g. "Aug 2, 2026". */
  readonly openedOn: string;
  /** Version the request was raised against, e.g. "V4"; undefined for older requests without one. */
  readonly versionTag: string | undefined;
  /** E.g. "v4 published May 9, 2020 · Latest published version"; undefined when the version isn't in the history. */
  readonly versionDescription: string | undefined;
  /** E.g. "Due in 12 days"; undefined when the request has no enforced deadline or is closed. */
  readonly dueLabel: string | undefined;
  /** Status options to offer, excluding the current state. */
  readonly statusOptions: readonly {
    readonly value: PublicationFeedbackState;
    readonly label: string;
  }[];
  /** The status the user has picked but not yet saved, or `undefined`. */
  readonly selectedStatus: PublicationFeedbackState | undefined;
  /** Set the pending status selection. */
  readonly selectStatus: (state: PublicationFeedbackState | undefined) => void;
  /** Current comment draft. */
  readonly comment: string;
  /** Update the comment draft. */
  readonly setComment: (comment: string) => void;
  /** Plain-text length of the comment draft, used for the character count and size warning. */
  readonly commentLength: number;
  /** Validation error for the comment, or `undefined`. */
  readonly commentError: string | undefined;
  /** A failure from submitting the update (not validation), or `undefined`. */
  readonly submitError: string | undefined;
  /** Whether a comment is required to complete the pending action (closing, or reopening). */
  readonly isCommentRequired: boolean;
  /** Whether the primary action button should be enabled. */
  readonly isSubmitEnabled: boolean;
  /** True while an update is in flight. */
  readonly isSubmitting: boolean;
  /** Save the pending status change and/or comment (open feedback). */
  readonly save: () => void;
  /** Add a standalone comment without changing status (non-author on open feedback). */
  readonly addComment: () => void;
  /** Reopen closed feedback (requires a comment). */
  readonly reopen: () => void;
}

/**
 * Describes a deadline relative to now in whole days, e.g. "Due in 12 days".
 *
 * @param needUpdateBy The deadline.
 * @param now The current time.
 */
export function formatDueLabel(needUpdateBy: DateTime, now: DateTime): string {
  const days = Math.ceil(needUpdateBy.diff(now, 'days').days);
  if (days <= 0) return 'Overdue';
  return days === 1 ? 'Due in 1 day' : `Due in ${days} days`;
}

/**
 * Approximates the plain-text length of editor HTML the way the editor's own
 * `getText` would: strips tags, decodes HTML entities, drops zero-width spaces,
 * and trims. Uses the browser's own HTML parser so every named and numeric
 * entity is decoded the way it will actually render.
 */
function plainLength(comment: string): number {
  // Parse as HTML so tags are dropped and every entity is decoded natively.
  const text = new DOMParser().parseFromString(comment, 'text/html').body.textContent ?? '';

  return text.replace(/\u200B/g, '').trim().length;
}

/**
 * Drives one update-request item's editing controls: status change, comment,
 * and reopen. Authors (owner/co-author) and privileged roles may change status
 * on open feedback and reopen closed feedback; anyone may comment. A comment is
 * required when closing or reopening.
 *
 * @param feedback The item being edited.
 * @param onUpdated Called after a successful server update so the list can patch in place.
 * @param publishedVersion The version the request was raised against, when it is in the history.
 */
export function useUpdateRequestItemViewModel(
  feedback: PublicationFeedback,
  onUpdated: (update: FeedbackUpdate) => void,
  publishedVersion?: PublishedVersion,
): UpdateRequestItemViewModel | undefined {
  const service = useContentEditorService();
  const mpl = useMplServiceClient();
  const { userId, roles } = useCurrentUser();
  const coAuthors = useCoAuthors();
  const document = useLoadedDocument();

  const contentId = document?.contentId;

  const [state, setState] = useState<PublicationFeedbackState>(feedback.state);
  const [selectedStatus, setSelectedStatus] = useState<PublicationFeedbackState | undefined>(
    undefined,
  );

  // When the parent patches this item's state (e.g. another edit resolves it),
  // adopt the new server state during render rather than mirroring it in an
  // effect. `lastServerState` tracks the prop we last synced so an in-flight
  // optimistic update isn't clobbered by an unchanged prop.
  const [lastServerState, setLastServerState] = useState<PublicationFeedbackState>(feedback.state);
  if (feedback.state !== lastServerState) {
    setLastServerState(feedback.state);
    setState(feedback.state);
  }
  const [comment, setComment] = useState('');
  const [commentError, setCommentError] = useState<string | undefined>(undefined);
  const [submitError, setSubmitError] = useState<string | undefined>(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isClosed = isFeedbackClosed(state);
  // Ownership comes from the loaded document, not the workflow: the tab renders
  // on published content, where there is no workflow to read the owner from.
  const isAuthor =
    userId === document?.author?.userId || coAuthors.some((c) => c.userId === userId);
  // Only admins are privileged to change status like an author.
  const isPrivileged = roles.has(UserRoles.Admin);
  const canChangeStatus = (isAuthor || isPrivileged) && !isClosed;
  // The backend requires edit permission for any state change, reopening included.
  const canReopen = (isAuthor || isPrivileged) && isClosed;

  const statusOptions = useMemo(() => STATUS_OPTIONS.filter((o) => o.value !== state), [state]);

  const isStatusChange = selectedStatus !== undefined && selectedStatus !== state;
  const isClosingStatus =
    selectedStatus === PublicationFeedbackState.RESOLVED ||
    selectedStatus === PublicationFeedbackState.NO_CHANGE_NEEDED;
  // Closing (author) or reopening (closed item) both require a comment.
  const isCommentRequired = (canChangeStatus && isClosingStatus && isStatusChange) || canReopen;

  const commentLength = plainLength(comment);
  const hasComment = commentLength > 0;
  const isCommentLengthValid =
    commentLength >= COMMENT_MIN_LENGTH && commentLength <= COMMENT_MAX_LENGTH;

  const isSubmitEnabled = (() => {
    if (isSubmitting) return false;
    if (isClosed) return hasComment && isCommentLengthValid;
    if (canChangeStatus) {
      if (!isStatusChange && !hasComment) return false;
      if (isCommentRequired && !isCommentLengthValid) return false;
      if (hasComment && !isCommentLengthValid) return false;
      return true;
    }
    return hasComment && isCommentLengthValid;
  })();

  const validateComment = useCallback((): boolean => {
    if (commentLength < COMMENT_MIN_LENGTH) {
      setCommentError(`${COMMENT_MIN_LENGTH} characters required`);
      return false;
    }
    if (commentLength > COMMENT_MAX_LENGTH) {
      setCommentError(`Maximum ${COMMENT_MAX_LENGTH} characters allowed`);
      return false;
    }
    setCommentError(undefined);
    return true;
  }, [commentLength]);

  const submit = useCallback(
    async (config: { state?: PublicationFeedbackState; comment?: string }) => {
      if (!contentId) return;
      const normalizedConfig = { ...config };
      if (normalizedConfig.comment !== undefined && plainLength(normalizedConfig.comment) === 0) {
        delete normalizedConfig.comment;
      }
      const previousState = state;
      if (normalizedConfig.state) setState(normalizedConfig.state);
      setIsSubmitting(true);
      setCommentError(undefined);
      setSubmitError(undefined);
      try {
        const response = await mpl.updatePublicationFeedback(
          contentId,
          feedback.feedbackId,
          normalizedConfig,
        );
        setComment('');
        setSelectedStatus(undefined);
        onUpdated({
          feedbackId: feedback.feedbackId,
          newState: normalizedConfig.state,
          activity: response?.activity,
        });
      } catch (err) {
        if (normalizedConfig.state) setState(previousState);
        log.error({ err, contentId }, 'Failed to update publication feedback');
        setSubmitError(
          err instanceof Error ? err.message : 'An unexpected error occurred. Please report a bug.',
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [contentId, state, mpl, feedback.feedbackId, onUpdated],
  );

  const save = useCallback(() => {
    if (isCommentRequired && !validateComment()) return;
    if (hasComment && !validateComment()) return;
    if (!isStatusChange && !hasComment) return;
    const config: { state?: PublicationFeedbackState; comment?: string } = {};
    if (isStatusChange) config.state = selectedStatus;
    if (hasComment) config.comment = comment;
    void submit(config);
  }, [
    isCommentRequired,
    validateComment,
    hasComment,
    isStatusChange,
    selectedStatus,
    comment,
    submit,
  ]);

  const addComment = useCallback(() => {
    if (!validateComment()) return;
    void submit({ comment });
  }, [validateComment, submit, comment]);

  const reopen = useCallback(() => {
    if (!validateComment()) return;
    void submit({ state: PublicationFeedbackState.CREATED, comment });
  }, [validateComment, submit, comment]);

  // A parent-teardown render can transiently clear the document before this
  // view unmounts; return undefined so the view renders null instead of throwing.
  if (!document) return undefined;

  const { workflowId, needUpdateBy } = feedback;
  const versionTag = workflowId !== undefined ? `V${workflowId}` : undefined;
  const versionDescription =
    workflowId !== undefined && publishedVersion
      ? `v${workflowId} published ${publishedVersion.publicationDate.toLocaleString(DateTime.DATE_MED)}` +
        (publishedVersion.isLatest ? ' · Latest published version' : '')
      : undefined;

  return {
    state,
    isClosed,
    canChangeStatus,
    canReopen,
    submitterName: feedback.userProfile?.name ?? 'Unknown',
    openedOn: feedback.createdAt?.toLocaleString(DateTime.DATE_MED) ?? '',
    versionTag,
    versionDescription,
    dueLabel: needUpdateBy && !isClosed ? formatDueLabel(needUpdateBy, DateTime.now()) : undefined,
    statusOptions,
    selectedStatus,
    selectStatus: setSelectedStatus,
    comment,
    setComment,
    commentLength,
    commentError,
    submitError,
    isCommentRequired,
    isSubmitEnabled,
    isSubmitting,
    save,
    addComment,
    reopen,
  };
}
